import { useEffect, useMemo } from 'react';
import { NextRouter, useRouter } from 'next/router';

import { Breadcrumb, IArticleByGroupDetail, IKlikStoreProduct } from 'src/interface';
import { trackInsiderObject } from 'src/utils/analytics/insider';
import { kebabCase } from 'src/utils/func';

type ListingType = 'meetDoctors' | 'doctors' | 'services' | 'articles' | 'products';
type MapMapper = Record<ListingType, (d: any) => Record<string, any>>;

const useInsiderTrackingCategory = (listingType?: ListingType, itemsListing?: any, page?: any) => {
	const router = useRouter() as NextRouter & {
		components: any;
	};

	const breadcrumb = useMemo(
		() =>
			router?.components?.[router.pathname]?.props?.pageProps?.pageHeaderContent?.breadcrumbs?.map(
				(v: Breadcrumb) => kebabCase(v?.label),
			),
		[router.isReady],
	);

	useEffect(() => {
		if (router.asPath === '/') {
			trackInsiderObject('page')({
				type: 'Home',
			});
		} else {
			trackInsiderObject('page')({
				type: page ?? 'Category',
				breadcrumb,
			});
		}
	}, [router.asPath]);

	useEffect(() => {
		if (!!itemsListing && !!listingType) {
			const mapMapper: MapMapper = {
				services: (data: any) => ({
					id: data?.id,
					name: data?.name,
					// eslint-disable-next-line max-len
					url: `${window.location.origin}/${
						data?.medical_facility_type_code
							? `${data?.medical_facility_type_code}/${data?.medical_facility_slug}`
							: data.type_code
					}/${data?.slug}`,
				}),
				meetDoctors: (data: any) => ({
					id: data?.master_uid,
					name: data?.name,
					url: `${window.location.origin}/temu-dokter/dokter/${data?.slug}`,
				}),
				doctors: (data: any) => ({
					id: data?.uid,
					name: data?.display_name,
					unit_sales_price: data?.final_price,
					unit_price: data?.final_price,
					product_image_url: data?.photo,
					taxonomy: [...breadcrumb, kebabCase(data?.speciality)],
					url: `${window.location.origin}/dokter/${data?.slug || data?.id}`,
				}),
				articles: (data: IArticleByGroupDetail) => ({
					id: data?.uid,
					name: data?.title,
					product_image_url: data?.media_images?.[0]?.original,
				}),
				products: (data: IKlikStoreProduct) => ({
					id: data?.id,
					name: data?.name,
					taxonomy: breadcrumb ?? [],
					currency: 'IDR',
					unit_price: data?.prices?.selling_price,
					unit_sale_price: data?.prices?.special_price,
					unit_sales_price: data?.prices?.special_price,
					url: `${window.location.origin}/kalstore/s/${data?.merchant?.slug}/${data?.slug}`,
					stock: data?.merchant?.stock ?? 1,
					color: '',
					size: `${data?.weight} kg`,
					product_image_url: data?.images?.[0]?.thumbnail ?? data?.image,
				}),
			};

			trackInsiderObject('listing')({
				items: itemsListing?.map((item: any) => {
					const data = item?.document || item;

					return {
						unit_sales_price: data?.price,
						unit_price: data?.normal_price,
						product_image_url: data?.photo_highres,
						taxonomy: breadcrumb ?? [],
						...mapMapper[listingType](data),
					};
				}),
			});
		}
	}, [itemsListing]);
};

export default useInsiderTrackingCategory;
